import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO.js';
import { useAuthStore } from '../stores/auth-store.js';
import BottomNav from '../components/BottomNav.js';
import { usePageTracker } from '../hooks/usePageTracker.js';

const API = import.meta.env.PROD
  ? 'https://cardealeros-api.bowling-anthony.workers.dev/v1'
  : '/v1';

interface Vehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  body_style: string | null;
  mileage: number | null;
  asking_price_cents: number | null;
  exterior_color: string | null;
  condition: string | null;
  title: string | null;
  created_at: string;
  fuel_type: string | null;
  transmission: string | null;
  dealer_name: string | null;
  dealer_city: string | null;
  dealer_state: string | null;
  photo_url: string | null;
  photo_count: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function formatPrice(cents: number | null) {
  if (!cents) return '';
  return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatMiles(miles: number | null) {
  if (!miles) return '';
  return miles.toLocaleString() + ' mi';
}

const BODY_FILTERS = ['SUV', 'Truck', 'Sedan', 'Coupe', 'Van', 'Wagon'];
const PRICE_FILTERS = [
  { label: 'Under $10k', max: 10000 },
  { label: 'Under $15k', max: 15000 },
  { label: 'Under $25k', max: 25000 },
  { label: 'Under $50k', max: 50000 },
];
const STATE_FILTERS = ['TN', 'TX', 'FL', 'CA', 'GA', 'NC', 'IL', 'OH', 'PA', 'NY'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_low', label: 'Price: Low' },
  { value: 'price_high', label: 'Price: High' },
  { value: 'miles_low', label: 'Lowest Miles' },
];

function SkeletonCard() {
  return (
    <div className="relative aspect-[4/3] rounded-xl bg-surface-800 border border-surface-700 animate-pulse" />
    );
}

export default function BrowsePage() {
  usePageTracker('/browse');

  const { token, user } = useAuthStore();
  const isLoggedIn = !!token;
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [activeBody, setActiveBody] = useState(params.get('body_style') ?? '');
  const [activePrice, setActivePrice] = useState(params.get('price_max') ?? '');
  const [activeState, setActiveState] = useState(params.get('state') ?? '');
  const [sort, setSort] = useState(params.get('sort') ?? 'newest');

  const fetchVehicles = useCallback(async (page = 1, append = false) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('limit', '24');
    qs.set('sort', sort);
    if (search) qs.set('q', search);
    if (activeBody) qs.set('body_style', activeBody);
    if (activePrice) qs.set('price_max', activePrice);
    if (activeState) qs.set('state', activeState);

    try {
      const res = await fetch(`${API}/public/vehicles?${qs}`);
      const json = await res.json() as { success: boolean; data: Vehicle[]; pagination: Pagination };
      if (json.success) {
        setVehicles((prev) => append ? [...prev, ...json.data] : json.data);
        setPagination(json.pagination);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, activeBody, activePrice, activeState, sort]);

  useEffect(() => {
    fetchVehicles(1);
  }, [fetchVehicles]);

  // Debounced search
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      const p = new URLSearchParams(params);
      if (searchInput) p.set('q', searchInput); else p.delete('q');
      setParams(p, { replace: true });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  function toggleBody(body: string) {
    const next = activeBody === body ? '' : body;
    setActiveBody(next);
    const p = new URLSearchParams(params);
    if (next) p.set('body_style', next); else p.delete('body_style');
    setParams(p, { replace: true });
  }

  function togglePrice(max: string) {
    const next = activePrice === max ? '' : max;
    setActivePrice(next);
    const p = new URLSearchParams(params);
    if (next) p.set('price_max', next); else p.delete('price_max');
    setParams(p, { replace: true });
  }

  function toggleState(st: string) {
    const next = activeState === st ? '' : st;
    setActiveState(next);
    const p = new URLSearchParams(params);
    if (next) p.set('state', next); else p.delete('state');
    setParams(p, { replace: true });
  }

  const hasMore = pagination && pagination.page < pagination.totalPages;

  return (
    <div className="min-h-screen bg-surface-900 text-white">
      <SEO title="Browse Cars for Sale" description="Browse cars for sale near you. Find deals from individual sellers and local dealers." path="/browse" />
      {/* Header */}
      <header className="sticky top-0 z-30 bg-surface-900/95 backdrop-blur-sm border-b border-surface-800/60">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight">AutoCarPost</Link>
          <div className="flex items-center gap-3">
            <Link to="/go" className="btn-primary text-sm !px-4 !py-2 !rounded-xl">
              Sell Your Car
            </Link>
            {isLoggedIn ? (
              <Link to="/dashboard" className="flex items-center gap-2 text-sm font-medium text-surface-300 hover:text-white transition-colors">
                <span className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-xs font-bold text-white">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
                My Account
              </Link>
            ) : (
              <Link to="/login" className="text-surface-400 hover:text-white text-sm font-medium transition-colors">
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-5">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search make, model, or keyword..."
            className="input w-full text-base"
          />
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-2">
          {BODY_FILTERS.map((b) => (
            <button
              key={b}
              onClick={() => toggleBody(b)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeBody === b
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-500'
              }`}
            >
              {b}
            </button>
          ))}
          <div className="w-px h-5 bg-surface-700 shrink-0 mx-1" />
          {PRICE_FILTERS.map((p) => (
            <button
              key={p.max}
              onClick={() => togglePrice(String(p.max))}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activePrice === String(p.max)
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-500'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="w-px h-5 bg-surface-700 shrink-0 mx-1" />
          {STATE_FILTERS.map((st) => (
            <button
              key={st}
              onClick={() => toggleState(st)}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeState === st
                  ? 'bg-brand-600 border-brand-500 text-white'
                  : 'bg-surface-800 border-surface-700 text-surface-400 hover:border-surface-500'
              }`}
            >
              {st}
            </button>
          ))}
        </div>

        {/* Sort + count */}
        <div className="flex items-center justify-between mb-5">
          <p className="text-surface-500 text-sm">
            {pagination ? `${pagination.total} car${pagination.total !== 1 ? 's' : ''}` : ''}
          </p>
          <select
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              const p = new URLSearchParams(params);
              p.set('sort', e.target.value);
              setParams(p, { replace: true });
            }}
            className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-1.5 text-sm text-surface-300 focus:outline-none focus:border-brand-500"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Vehicle grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-surface-500 text-lg mb-2">No cars found</p>
            <p className="text-surface-600 text-sm mb-6">Try different filters or post yours!</p>
            <Link to="/go" className="btn-primary inline-block">Post Your Car Free</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((v) => (
                <Link
                  key={v.id}
                  to={`/browse/${v.id}`}
                  className="relative aspect-[4/3] rounded-xl overflow-hidden group border border-surface-700 hover:border-brand-500 transition-colors"
                >
                  {v.photo_url ? (
                    <img
                      src={v.photo_url}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-surface-800 flex items-center justify-center">
                      <svg className="w-12 h-12 text-surface-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.41a2.25 2.25 0 013.182 0l2.909 2.91m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  {v.photo_count > 1 && (
                    <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-0.5 text-xs text-white/80">
                      {v.photo_count} photos
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    {v.asking_price_cents && (
                      <p className="text-white font-bold text-lg leading-tight">{formatPrice(v.asking_price_cents)}</p>
                    )}
                    <p className="text-white/90 text-sm font-medium">{v.year} {v.make} {v.model}</p>
                    <p className="text-white/50 text-xs mt-0.5">
                      {formatMiles(v.mileage)}
                      {v.mileage && v.dealer_city ? ' \u00b7 ' : ''}
                      {[v.dealer_city, v.dealer_state].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => fetchVehicles((pagination?.page ?? 0) + 1, true)}
                  disabled={loadingMore}
                  className="btn-secondary !px-8"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isLoggedIn && <BottomNav />}

        <footer className="border-t border-surface-800/60 px-6 py-8 mt-12 text-center">
        <div className="flex items-center justify-center gap-6 mb-4">
          <Link to="/go" className="text-surface-500 hover:text-white text-xs transition-colors">Sell Your Car</Link>
          <Link to="/" className="text-surface-500 hover:text-white text-xs transition-colors">Home</Link>
          <Link to="/login" className="text-surface-500 hover:text-white text-xs transition-colors">Sign In</Link>
        </div>
        <p className="text-surface-600 text-xs">&copy; {new Date().getFullYear()} AutoCarPost</p>
      </footer>
    </div>
  );
}
